import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion, useScroll, useTransform, useReducedMotion,
  useMotionValueEvent, useInView, useMotionValue, animate, AnimatePresence,
} from "framer-motion";
import {
  ArrowRight, Check, ArrowDown, TrendingUp, TrendingDown, ChefHat, ScanLine, QrCode, Sparkles,
  Mail, Copy, Languages, Printer, Pause, Play, Beef, ChevronRight,
} from "lucide-react";
import { PHOTOS } from "../lib/photos";

/* ============================================================
   RentaScan — Editorial Landing v4 (animée)
   Framer Motion · prefers-reduced-motion respecté
   ============================================================ */

const EASE = [0.16, 1, 0.3, 1];

/* Reusable variants ------------------------------------------ */
const useReveal = () => {
  const reduce = useReducedMotion();
  return reduce
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.3 } } }
    : {
        hidden: { opacity: 0, y: 35 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
      };
};

const useStagger = (delay = 0.12) => ({
  hidden: {},
  show:   { transition: { staggerChildren: delay, delayChildren: 0.05 } },
});

/* Wrap a block into an in-view section that reveals children  */
function Section({ children, className = "", id, staggerDelay = 0.12, threshold = 0.2 }) {
  const reveal = useReveal();
  return (
    <motion.section
      id={id}
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: threshold }}
      variants={useStagger(staggerDelay)}
    >
      {/* Descendants that need to reveal must use `data-reveal` OR wrap themselves in <Reveal /> */}
      {children}
    </motion.section>
  );
}

function Reveal({ children, className = "", as = "div" }) {
  const v = useReveal();
  const Comp = motion[as] || motion.div;
  return (
    <Comp variants={v} className={className}>
      {children}
    </Comp>
  );
}

/* Animated numeric counter — plays once (or on resetKey change). */
function Counter({ value, prefix = "", suffix = "", decimals = 0, duration = 1.6, resetKey = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: resetKey === 0, amount: 0.5 });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(prefix + Number(0).toFixed(decimals).replace(".", ",") + suffix);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!inView && resetKey === 0) return;
    if (reduce) { setDisplay(prefix + Number(value).toFixed(decimals).replace(".", ",") + suffix); return; }
    mv.set(0);
    const controls = animate(mv, value, { duration, ease: EASE });
    const unsub = mv.on("change", (v) => setDisplay(prefix + v.toFixed(decimals).replace(".", ",") + suffix));
    return () => { controls.stop(); unsub(); };
  }, [inView, value, prefix, suffix, decimals, duration, mv, reduce, resetKey]);
  return <span ref={ref}>{display}</span>;
}

/* ============================================================ */
export default function Landing() {
  return (
    <div className="bg-[#F4F5EF] text-[#111]" data-testid="landing-page">
      <TopNav />
      <Hero />
      <SocialProofBar />
      <StoryScroll />
      <SuppliersSection />
      <RoiBand />
      <Pricing />
      <FooterCta />
      <SiteFooter />
    </div>
  );
}

/* ---------- Top navigation with scroll morph ---------------- */
function TopNav() {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 60));

  return (
    <motion.header
      initial={false}
      animate={{
        backgroundColor: scrolled ? "rgba(255,255,255,0.95)" : "rgba(11,31,51,0)",
        borderBottomColor: scrolled ? "#CDD7E3" : "rgba(255,255,255,0)",
        boxShadow: scrolled ? "0 2px 8px rgba(11,31,51,0.08)" : "0 0 0 rgba(0,0,0,0)",
        color: scrolled ? "#0B1F33" : "#ffffff",
      }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className="fixed inset-x-0 top-0 z-40 border-b backdrop-blur-md"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <Wordmark tone={scrolled ? "light" : "dark"} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium">
          {[
            ["Scan de factures", "#story"],
            ["Tarifs", "#tarifs"],
          ].map(([label, href]) => (
            <a key={label} href={href} style={{ color: "inherit", opacity: 0.85 }} className="hover:!opacity-100 transition-opacity">
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/connexion" className="hidden sm:inline text-[14px] font-semibold" style={{ color: "inherit", opacity: 0.9 }} data-testid="nav-login-btn">Se connecter</Link>
          <Link to="/inscription?plan=monthly" className="btn-orange !h-11 !px-3 sm:!px-5 !text-[13px] sm:!text-[14px]" data-testid="nav-signup-btn">
            <span>Essayez gratuitement</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.header>
  );
}

import { Wordmark as BrandWordmark } from "../components/Brand";
import ProductPriceChart from "../components/ProductPriceChart";

function Wordmark({ tone = "light" }) {
  return <BrandWordmark tone={tone} size="md" />;
}

/* ============ HERO with entrance + parallax ================ */
function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const photoY = useTransform(scrollY, [0, 700], [0, reduce ? 0 : 40]);
  const cutoutY = useTransform(scrollY, [0, 700], [0, reduce ? 0 : -25]);

  const hero = reduce
    ? { hidden: { opacity: 0 }, show: (i) => ({ opacity: 1, transition: { duration: 0.3, delay: i * 0.05 } }) }
    : {
        hidden: { opacity: 0, y: 30 },
        show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15, ease: EASE } }),
      };
  const photoIn = reduce
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.3 } } }
    : { hidden: { opacity: 0, x: 60, scale: 0.96 }, show: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.9, ease: EASE, delay: 0.1 } } };

  return (
    <section ref={ref} className="relative bg-[#0B1F33] text-white overflow-hidden min-h-[720px] lg:min-h-[820px]" data-testid="landing-hero">
      {/* Right-side hero photo */}
      <motion.div className="absolute inset-0 lg:left-[52%] xl:left-[54%] overflow-hidden" style={{ y: photoY }} initial="hidden" animate="show" variants={photoIn}>
        <img src={PHOTOS.bistroInterior} alt="Restaurateur dans son bistro" className="w-full h-full object-cover" loading="eager" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B1F33] via-transparent to-transparent lg:from-[#0B1F33]/95 lg:via-[#0B1F33]/20 lg:to-transparent" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 pb-24 lg:pt-52 lg:pb-32 grid lg:grid-cols-[minmax(0,600px)_1fr] gap-10 items-end lg:min-h-[820px]">
        <div className="max-w-[600px]">
          <motion.div custom={0} initial="hidden" animate="show" variants={hero}
            className="inline-flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.16em] text-[#FFD166]">
            <span className="w-6 h-[2px] bg-[#FF9F1C]" /> Restaurants, Bars
          </motion.div>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={hero} className="display-1 mt-6 text-white">
            Votre marge diminue&nbsp;?<br />
            <MarkerWord>Optimisez-la&nbsp;!</MarkerWord>
          </motion.h1>
          <div className="relative mt-7 max-w-[520px]">
            {/* Voile discret animé — n'apparaît que sur mobile/tablette où
                le fond visuel derrière le texte peut réduire la lisibilité.
                Rebords fondus via masque radial : plus de coupe franche,
                le voile s'estompe naturellement sur tous les côtés. */}
            <motion.span
              aria-hidden
              className="lg:hidden absolute -inset-x-6 -inset-y-5 pointer-events-none"
              style={{
                background: "radial-gradient(ellipse 75% 70% at 50% 50%, rgba(11,31,51,0.62) 0%, rgba(11,31,51,0.55) 45%, rgba(11,31,51,0.25) 78%, rgba(11,31,51,0) 100%)",
                filter: "blur(2px)",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: reduce ? 0.2 : 1.2, delay: reduce ? 0.15 : 0.6, ease: EASE }}
            />
            {/* Étapes présentées en points qui apparaissent successivement.
                Chaque item se révèle avec un décalage (staggered fade + slide). */}
            <motion.ul
              initial="hidden" animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.14, delayChildren: reduce ? 0 : 0.5 } } }}
              className="relative text-[16px] sm:text-[17px] leading-[1.55] text-white/95 space-y-2.5"
            >
              {[
                "Scannez vos factures",
                "Repérez les hausses de prix",
                "Mesurez l'impact sur vos recettes",
                "Ajustez votre menu en conséquence",
                "Négociez avec vos fournisseurs",
              ].map((item) => (
                <motion.li
                  key={item}
                  variants={{ hidden: { opacity: 0, x: -12 }, show: { opacity: 1, x: 0 } }}
                  transition={{ duration: reduce ? 0.2 : 0.55, ease: EASE }}
                  className="flex items-start gap-3"
                >
                  <Check className="w-4 h-4 mt-1 shrink-0 text-[#FFD166]" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
          <motion.div custom={3} initial="hidden" animate="show" variants={hero} className="mt-9 flex flex-wrap gap-3">
            <motion.div whileHover={reduce ? {} : { y: -2 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.15 }}>
              <Link to="/inscription?plan=monthly" data-testid="hero-cta-primary" className="btn-orange">
                Essayez gratuitement <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.a whileHover={reduce ? {} : { y: -2 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.15 }}
              href="#story" data-testid="hero-cta-secondary" className="btn-outline-white">Voir comment ça marche</motion.a>
          </motion.div>
          <motion.div custom={4} initial="hidden" animate="show" variants={hero} className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-white/70 font-medium">
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#FFD166]" /> 14 jours gratuits</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#FFD166]" /> Sans engagement</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-[#FFD166]" /> Sans carte bancaire</span>
          </motion.div>
        </div>

        <motion.div style={{ y: cutoutY }} initial={{ opacity: 0, x: 80, scale: 0.94 }} animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }} transition={{ duration: 1.0, ease: EASE, delay: 0.25 }} className="hidden lg:block relative">
          <ProductCutout />
        </motion.div>
      </div>

      <motion.a href="#story" aria-hidden initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ delay: 1.2 }}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2 text-[11px] uppercase tracking-widest text-white/60">
        <span>Découvrir</span>
        <ArrowDown className="w-3.5 h-3.5 animate-bounce" style={{ animationDuration: "1.4s" }} />
      </motion.a>
    </section>
  );
}

/* Animated marker underline — plays once when hero appears */
function MarkerWord({ children }) {
  const reduce = useReducedMotion();
  return (
    <span className="relative inline-block">
      <span className="relative z-10 font-black text-[#0B1F33] px-1">{children}</span>
      <motion.span aria-hidden className="absolute left-0 right-0 rounded-[3px]"
        style={{ background: "rgba(255,209,102,1)", top: "6%", bottom: "4%", zIndex: 0, transformOrigin: "left", boxShadow: "0 6px 22px rgba(255,209,102,0.35)" }}
        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: reduce ? 0.3 : 0.9, ease: EASE, delay: reduce ? 0.15 : 0.85 }} />
    </span>
  );
}

/* Animated marker highlight — plays once when the phrase enters the viewport.
   Used inside story-step descriptions to bring a key phrase forward as the
   user scrolls. Left-to-right sweep, same visual language as MarkerWord. */
function MarkerLine({ children }) {
  const reduce = useReducedMotion();
  return (
    <motion.span
      className="inline"
      style={{
        backgroundImage: "linear-gradient(180deg, transparent 0%, transparent 50%, rgba(255,209,102,0.65) 50%, rgba(255,209,102,0.65) 100%)",
        backgroundRepeat: "no-repeat",
        WebkitBoxDecorationBreak: "clone",
        boxDecorationBreak: "clone",
      }}
      initial={{ backgroundSize: "0% 100%" }}
      whileInView={{ backgroundSize: "100% 100%" }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: reduce ? 0.3 : 0.9, ease: EASE, delay: reduce ? 0 : 0.2 }}
    >
      {children}
    </motion.span>
  );
}

/* Story-step description rendered as a staggered bullet list. Chaque
   point apparaît successivement quand le bloc entre dans la fenêtre,
   respect prefers-reduced-motion. */
function StepPoints({ items }) {
  const reduce = useReducedMotion();
  return (
    <motion.ul
      className="mt-5 space-y-2.5 text-[16px] sm:text-[17px] leading-relaxed text-[#5E6875] max-w-[500px]"
      initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: reduce ? 0 : 0.15 } } }}
    >
      {items.map((it, k) => (
        <motion.li
          key={k}
          variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0 } }}
          transition={{ duration: reduce ? 0.2 : 0.5, ease: EASE }}
          className="flex items-start gap-3"
        >
          <Check className="w-4 h-4 mt-1 shrink-0 text-[#FF9F1C]" />
          <span>{it}</span>
        </motion.li>
      ))}
    </motion.ul>
  );
}

/* Faux app cutout — product-in-situation */
function ProductCutout() {
  return (
    <div className="relative w-full max-w-[440px] ml-auto -mb-8">
      <div className="bg-white text-[#111] rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.45)] p-5 rotate-[-2deg]">
        <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#1769E0]">Facture Charcuterie · analysée</div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            <div className="text-[15px] font-extrabold">Coppa AOP · Prix / kg</div>
            <div className="text-[12px] text-[#5E6875]">FA-2026-0138 · 09/09</div>
          </div>
          <span className="pill pill-up">+16 %</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 items-end">
          <div>
            <div className="text-[9.5px] uppercase text-[#5E6875] font-bold tracking-wider">Ancien</div>
            <div className="mt-0.5 text-[17px] font-extrabold tabular-nums line-through decoration-[#D84343] decoration-2">16,00 €</div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase text-[#5E6875] font-bold tracking-wider">Nouveau</div>
            <div className="mt-0.5 text-[22px] font-extrabold tabular-nums text-[#0B1F33]">18,56 €</div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase text-[#5E6875] font-bold tracking-wider">Impact / an</div>
            <div className="mt-0.5 text-[16px] font-extrabold tabular-nums text-[#D84343]">−612 €</div>
          </div>
        </div>
        <div className="mt-4 rounded-xl bg-[#F4F5EF] px-3 py-3 border border-[#CDD7E3]">
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Plats concernés</div>
          <div className="mt-2 space-y-1.5 text-[13px]">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5"><ChefHat className="w-3.5 h-3.5 text-[#1769E0]" /> Planche de charcuterie</span>
              <span className="font-semibold text-[#F58F06]">▲ +0,7 %</span>
            </div>
          </div>
        </div>
        <button className="mt-4 w-full h-11 rounded-lg bg-[#FF9F1C] text-[#0B1F33] text-[13.5px] font-bold inline-flex items-center justify-center gap-2">
          Mettre à jour la carte <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="absolute -bottom-14 -left-14 w-[210px] rounded-xl bg-white shadow-[0_20px_50px_rgba(0,0,0,0.35)] rotate-[7deg] overflow-hidden border border-[#0B1F33]/10">
        {/* Menu masthead — clearer branding as an actual restaurant menu */}
        <div className="bg-[#0B1F33] px-3 py-2.5 text-center">
          <div className="text-[8.5px] uppercase tracking-[0.22em] font-bold text-[#FFD166]">Bistro du Quai</div>
          <div className="mt-0.5 font-heading text-white text-[15px] font-extrabold leading-none tracking-tight">La Carte</div>
          <div className="mt-1 mx-auto w-8 h-[1.5px] bg-[#FF9F1C]" />
        </div>
        <div className="px-3 pt-2 pb-3">
          <div className="text-[8.5px] uppercase tracking-[0.16em] font-bold text-[#5E6875] mb-1.5">Plats · Prix TTC</div>
          <div className="space-y-1 text-[10.5px] font-medium text-[#111]">
            <div className="flex justify-between"><span>Salade César</span><span className="font-bold tabular-nums">12,00 €</span></div>
            <div className="flex justify-between bg-[#FFF0D2] px-1 rounded"><span>Planche de charcuterie</span><span className="font-bold tabular-nums">19,00 €</span></div>
            <div className="flex justify-between"><span>Bavette</span><span className="font-bold tabular-nums">22,00 €</span></div>
          </div>
          <div className="mt-2 pt-2 border-t border-dashed border-[#CDD7E3] flex items-center justify-between text-[9.5px] text-[#5E6875]">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#24845B] animate-pulse" />QR live</span>
            <QrCode className="w-4 h-4 text-[#0B1F33]" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Social proof strip ---------- */
function SocialProofBar() {
  return (
    <Section className="bg-[#0B1F33] text-white border-t border-white/8" threshold={0.4}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <Reveal className="flex flex-wrap gap-x-10 gap-y-3 text-[13px] text-white/70">
          <span><b className="text-white">80 %</b> de temps admin en moins</span>
          <span><b className="text-white">Contrôle de vos marges</b> en temps réel</span>
          <span><b className="text-white">Moins d'1 minute</b> par facture</span>
        </Reveal>
      </div>
    </Section>
  );
}

/* ============================================================
   STORY SCROLL — Sticky visual (desktop) + 4 étapes (mobile stack)
   ============================================================ */
function StoryScroll() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [step, setStep] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    // 6 sub-steps : le module 03 est doublé (Tableau de bord puis
    // Impact sur les recettes) — bandes égales 0-1/6 … 5/6-1.
    const s = p < 1/6 ? 0 : p < 2/6 ? 1 : p < 3/6 ? 2 : p < 4/6 ? 3 : p < 5/6 ? 4 : 5;
    setStep(s);
  });

  const STEPS = [
    { t: "Scannez vos factures. RentaScan fait le reste.",
      d: [
        (
          <div>
            <div>Analyse chaque produit :</div>
            <ul className="mt-1.5 ml-1 space-y-1 text-[15px] sm:text-[16px] text-[#5E6875]">
              <li className="flex items-start gap-2"><span className="mt-1 w-1 h-1 rounded-full bg-[#FF9F1C] shrink-0" /><span>Quantité</span></li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1 h-1 rounded-full bg-[#FF9F1C] shrink-0" /><span>Conditionnement</span></li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1 h-1 rounded-full bg-[#FF9F1C] shrink-0" /><span>Prix</span></li>
            </ul>
          </div>
        ),
        (<><MarkerLine>Compare les prix avec vos factures précédentes</MarkerLine></>),
      ],
      k: "01 · Analyse automatique" },
    { t: "Ajouté à votre catalogue, en un clic.",
      d: [
        (<><MarkerLine>Fiche produit enrichie automatiquement</MarkerLine></>),
        "Historique de prix complet",
        "Recettes associées mises à jour automatiquement",
      ],
      k: "02 · Catalogue enrichi" },
    { t: (
        <>
          Les hausses et les baisses, identifiées <MarkerLine>sans efforts</MarkerLine>.
        </>
      ),
      d: [
        "Produits en hausse ou en baisse immédiatement identifiés",
        (<><MarkerLine>Vision claire, nette et précise</MarkerLine></>),
      ],
      k: "03 · Tableau de bord" },
    { t: "Voyez les recettes impactées.",
      d: [
        "Alerte immédiate des recettes à optimiser",
        (<><MarkerLine>La visibilité sur votre marge n'a jamais été aussi bonne</MarkerLine></>),
      ],
      k: "04 · Impact sur vos recettes" },
    { t: "Fiches recettes intelligentes",
      d: [
        "Calcule le coût matière de chaque portion selon vos prix d'achat.",
        "Ajustez les doses et visualisez immédiatement l'impact sur votre marge.",
      ],
      k: "05 · Fiches recettes" },
    { t: "Un dossier de négociation prêt à l'envoi.",
      d: [
        "Simule votre perte potentielle chiffrée",
        "Détecte les hausses successives d'un même fournisseur",
        "Rédige pour vous un e-mail de négociation sur-mesure",
      ],
      k: "REMOVED_INLINE_INTEGRATION",
      hidden: true },
    { t: "Éditez et publiez votre menu.",
      d: [
        "Menu mis à jour automatiquement",
        "QR code et carte imprimable",
        "Traduction du menu en 7 langues",
      ],
      k: "06 · Éditeur de menu & QR code" },
  ];

  return (
    <section id="story" ref={ref} className="bg-white" data-testid="story-section" style={{ minHeight: reduce ? "auto" : "600vh" }}>
      {reduce ? (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 space-y-20">
          {STEPS.filter((s) => !s.hidden).map((s, i, arr) => (
            <Reveal key={i} className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="eyebrow">{s.k}</div>
                <h2 className="display-2 mt-3">{s.t}</h2>
                <StepPoints items={s.d} />
              </div>
              <StoryVisual step={STEPS.indexOf(s)} />
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="py-16 lg:py-24 space-y-16 lg:space-y-[75vh]">
            {STEPS.filter((s) => !s.hidden).map((s, i, arr) => (
              <div key={i} className="relative">
                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.65, ease: EASE }}>
                  <div className="eyebrow">{s.k}</div>
                  <h2 className="display-2 mt-3">{s.t}</h2>
                  <StepPoints items={s.d} />
                </motion.div>
                {/* Mobile/tablet inline visual — no display:none anywhere. Each step keeps its own
                    animated visual visible while the desktop layout stays with the sticky column.
                    Feb 2026 §« sheet visible en entier » — hauteur bornée par la fenêtre visible
                    afin que le déclencheur useInView (amount 0.9) puisse voir la feuille COMPLÈTE
                    sur téléphone court/tablette (`svh` = small viewport height, stable barre URL). */}
                <div className="lg:hidden mt-8 mx-auto w-full max-w-[440px]">
                  <div className="relative w-full" style={{ minHeight: "min(640px, calc(100svh - 160px))", height: "min(640px, calc(100svh - 160px))" }}>
                    <InlineStoryVisual step={STEPS.indexOf(s)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:block">
            <div className="sticky top-[60px] flex items-center justify-center" style={{ height: "calc(100vh - 120px)" }}>
              <StoryVisual step={step} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* Mobile/tablet inline version — same components as desktop, always rendered
   (never display:none) so animations are visible when scrolled into view. */
function InlineStoryVisual({ step }) {
  return (
    <>
      {step === 0 && <StepInvoice />}
      {step === 1 && <StepCatalog />}
      {step === 2 && <StepDashboard />}
      {step === 3 && <StepRecipesImpact />}
      {step === 4 && <StepRecipeCard />}
      {step === 6 && <StepMenuEditor />}
    </>
  );
}

/* Big visual reflecting current step. */
function StoryVisual({ step }) {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div className="relative w-full max-w-[520px]" style={{ aspectRatio: "1 / 1.414", maxHeight: "calc(100vh - 140px)" }}>
        <AnimatePresence mode="wait">
          {step === 0 && <StepInvoice key="s0" />}
          {step === 1 && <StepCatalog key="s1" />}
          {step === 2 && <StepDashboard key="s2" />}
          {step === 3 && <StepRecipesImpact key="s3r" />}
          {step === 4 && <StepRecipeCard key="s4c" />}
          {step === 5 && <StepMenuEditor key="s5" />}
        </AnimatePresence>
      </div>
    </div>
  );
}

const stepIn = {
  initial: { opacity: 0, y: 20, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: EASE } },
  exit:    { opacity: 0, y: -20, scale: 0.98, transition: { duration: 0.35, ease: EASE } },
};

/* --- Realistic invoice used for step 0 (with progressive scan + identification reveal) ---
 * Feb 2026 §« déclenchement au visible » — l'animation d'analyse ne démarre
 * plus au montage : on observe la POSITION RÉELLE de la feuille via
 * `useInView(amount:0.9)` combiné à un `rootMargin` négatif qui exclut la
 * navigation collante. On attend ensuite 550 ms (fin du `stepIn` d'entrée)
 * puis on lit `document.fonts.ready` pour s'assurer que la feuille a ses
 * dimensions finales avant d'enclencher le surlignage, l'identification et
 * la synthèse. `once: true` évite tout redémarrage lors d'un petit défilement.
 */
function StepInvoice() {
  const sheetRef = useRef(null);
  // amount 0.5 : l'animation démarre dès que le scroll atteint la moitié
  // de la feuille de facture (feedback utilisateur : « démarre quand on
  // arrive à 50 % de la facture »). On garde `once: false` pour rejouer
  // l'analyse à chaque re-entrée dans la fenêtre, et un léger rootMargin
  // négatif haut (-72 px) qui exclut la nav sticky de 60 px + marge.
  const inView = useInView(sheetRef, { amount: 0.5, once: false, margin: "-72px 0px 0px 0px" });
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    // Sortie du champ visible → on remet l'état à zéro pour permettre un
    // redémarrage propre au prochain retour dans la vue.
    if (!inView) {
      setScanning(false);
      return;
    }
    if (scanning) return;
    let cancelled = false;
    // 1) On attend la fin de l'animation d'entrée (stepIn ~ 0.5 s + easing).
    // 2) On attend `document.fonts.ready` — les polices web modifient la
    //    hauteur des lignes, donc les dimensions de la feuille.
    const start = async () => {
      try { await (document.fonts && document.fonts.ready); } catch { /* ignore */ }
      // Buffer post-entry pour laisser le pinceau d'entrée respirer.
      await new Promise((r) => setTimeout(r, 550));
      if (!cancelled) setScanning(true);
    };
    start();
    return () => { cancelled = true; };
  }, [inView, scanning]);

  return (
    <motion.div ref={sheetRef} {...stepIn} className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-hidden border border-[#CDD7E3] flex flex-col text-[#0B1F33]"
      data-testid="landing-invoice-sheet"
      style={{ backgroundImage: "linear-gradient(180deg, rgba(11,31,51,0.015) 1px, transparent 1px)", backgroundSize: "100% 24px" }}>
      {/* Narrative overlay: shown during scan to point at the columns the AI reads */}
      {scanning && (
        <motion.div
          initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5, ease: EASE }}
          className="absolute top-2 left-2 right-2 z-30 pointer-events-none"
          data-testid="invoice-scan-narrative"
        >
          <div className="rounded-md bg-[#0B1F33] text-white shadow-[0_10px_30px_rgba(11,31,51,0.35)] px-3 py-2 text-[10.5px] leading-tight border border-[#1769E0]/40">
            <div className="uppercase tracking-widest text-[9px] font-bold text-[#FFD166] mb-1">RentaScan lit ces lignes</div>
            <div className="flex flex-wrap gap-1">
              {["Désignation · Cond.", "Qté", "PU HT", "Total HT"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#1769E0]/25 border border-[#1769E0]/50 font-mono-num whitespace-nowrap">
                  <span className="w-1 h-1 rounded-full bg-[#FFD166]" />
                  {c}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-[#CDD7E3] flex items-start justify-between">
        <div>
          <div className="font-heading font-extrabold text-[15px]">CHARCUTERIE BÉARN SARL</div>
          <div className="text-[9.5px] text-[#5E6875] leading-tight mt-0.5">
            12 rue des Marchés · 64000 Pau<br />
            SIRET 812 456 789 00021 · TVA FR76 812456789<br />
            <span className="text-[#5E6875]/70">— Données de démonstration —</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#5E6875] font-bold">Facture</div>
          <div className="font-mono-num text-[13.5px] font-bold">FA-2026-0138</div>
          <div className="text-[10.5px] text-[#5E6875] mt-1">Le 09/09/2026</div>
          <div className="text-[10px] text-[#5E6875]">Client: BISTRO DU QUAI · CL-1204</div>
        </div>
      </div>
      {/* Table header */}
      <div className="px-5 pt-3 pb-1 grid grid-cols-[1fr_60px_60px_70px] gap-2 text-[9px] uppercase tracking-widest font-bold text-[#5E6875] border-b border-[#CDD7E3]">
        <span className={scanning ? "text-[#1769E0] relative" : ""}>
          Désignation · Cond.
          {scanning && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="absolute -top-2 right-1 w-1.5 h-1.5 rounded-full bg-[#FF9F1C] shadow-[0_0_10px_#FF9F1C]" />}
        </span>
        <span className={`text-right ${scanning ? "text-[#1769E0] relative" : ""}`}>
          Qté{scanning && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }} className="absolute -top-2 right-0 w-1.5 h-1.5 rounded-full bg-[#FF9F1C] shadow-[0_0_10px_#FF9F1C]" />}
        </span>
        <span className={`text-right ${scanning ? "text-[#1769E0] relative" : ""}`}>
          PU HT{scanning && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="absolute -top-2 right-0 w-1.5 h-1.5 rounded-full bg-[#FF9F1C] shadow-[0_0_10px_#FF9F1C]" />}
        </span>
        <span className={`text-right ${scanning ? "text-[#1769E0] relative" : ""}`}>
          Tot. HT{scanning && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }} className="absolute -top-2 right-0 w-1.5 h-1.5 rounded-full bg-[#FF9F1C] shadow-[0_0_10px_#FF9F1C]" />}
        </span>
      </div>
      {/* Lines */}
      <div className="px-5 py-2 space-y-[6px] text-[11px] font-mono-num flex-1 relative overflow-hidden">
        {scanning && (
          <motion.div className="absolute inset-x-4 h-[3px] pointer-events-none z-20 rounded"
            style={{ background: "linear-gradient(90deg, transparent, #2878FF, #1769E0, #2878FF, transparent)", boxShadow: "0 0 20px #1769E0, 0 0 40px rgba(23,105,224,0.35)" }}
            initial={{ top: "4%", opacity: 0 }}
            animate={{ top: "92%", opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.4, ease: "easeInOut", times: [0, 0.15, 0.85, 1] }} />
        )}
        {[
          ["COPPA IT 1.5KG",      "carton × 2",  "2",  "27,85", "55,70", false],
          ["PAIN CAMPAGNE 500G",  "sac × 12",    "12", "1,95",  "23,40", false],
          ["CR LIQ 30% 1L",       "brick × 6",   "6",  "3,20",  "19,20", false],
          ["TOM PEL 4/4 X6",      "carton × 4",  "4",  "8,70",  "34,80", false],
          ["RIC 45° 70CL X6",     "carton × 3",  "3",  "78,00", "234,00", true],
          ["EPAULE FUME 5KG",     "pièce",       "1",  "56,80", "56,80", false],
          ["ROSETTE LYON 2.5KG",  "pièce",       "1",  "46,50", "46,50", false],
        ].map(([n, cond, q, pu, tot, hi], i) => (
          <div key={n} className={`grid grid-cols-[1fr_60px_60px_70px] gap-2 items-center rounded ${hi && scanning ? "" : scanning ? "opacity-40" : ""}`}>
            {hi ? (
              <motion.div className="col-span-4 py-[3px] px-1 rounded"
                initial={{ backgroundColor: "rgba(255,159,28,0)" }}
                animate={{ backgroundColor: scanning ? "rgba(255,159,28,0.35)" : "rgba(255,159,28,0)" }}
                transition={{ delay: scanning ? 1.05 : 0, duration: 0.4 }}>
                <div className="grid grid-cols-[1fr_60px_60px_70px] gap-2 items-center font-bold">
                  <span>
                    {n} <span className="text-[9px] font-normal text-[#5E6875]">{cond}</span>
                    {scanning && (
                      <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.25, duration: 0.35 }}
                        className="ml-2 text-[9px] uppercase tracking-widest font-bold text-[#0B1F33] bg-[#FFD166] px-1.5 py-0.5 rounded">
                        Produit à identifier
                      </motion.span>
                    )}
                  </span>
                  <span className="text-right">{q}</span>
                  <span className="text-right">{pu}</span>
                  <span className="text-right">{tot}</span>
                </div>
              </motion.div>
            ) : (
              <>
                <span>{n} <span className="text-[9px] text-[#5E6875]">{cond}</span></span>
                <span className="text-right">{q}</span>
                <span className="text-right">{pu}</span>
                <span className="text-right">{tot}</span>
              </>
            )}
          </div>
        ))}
      </div>
      {/* Totals */}
      <div className="px-5 pt-2 pb-4 border-t border-[#CDD7E3] text-[11px] font-mono-num">
        <div className="flex justify-between"><span className="text-[#5E6875]">Sous-total HT</span><span className="font-semibold">470,40 €</span></div>
        <div className="flex justify-between"><span className="text-[#5E6875]">TVA 20 %</span><span className="font-semibold">94,08 €</span></div>
        <div className="flex justify-between mt-1 pt-1 border-t border-[#CDD7E3] font-bold text-[13px]"><span>Total TTC</span><span>564,48 €</span></div>
      </div>
      {/* Status */}
      <div className="px-5 pb-4">
        {scanning ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
            className="text-[10px] uppercase tracking-widest font-bold text-[#1769E0] inline-flex items-center gap-1.5">
            <ScanLine className="w-3 h-3" /> Analyse par RentaScan…
          </motion.div>
        ) : (
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">Document original</div>
        )}
      </div>

      {/* Progressive summary reveal — shows identification/conversion/comparison/catalog after the scan */}
      {scanning && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.45, ease: EASE }}
          className="mx-4 mb-4 rounded-md border border-[#1769E0]/25 bg-[#DCEBFF]/60 p-2.5"
          data-testid="invoice-summary-reveal"
        >
          <div className="text-[9px] uppercase tracking-widest font-bold text-[#1769E0]">Ligne identifiée</div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-heading text-[13.5px] font-extrabold text-[#0B1F33] leading-tight truncate">Pastis Ricard 45°</div>
              <div className="text-[10px] text-[#5E6875]">Carton × 6 · 70 cl · 4,2 L</div>
            </div>
            <div className="flex items-baseline gap-1.5 text-[11px] font-mono-num tabular-nums shrink-0">
              <span className="text-[#5E6875] line-through">67,20</span>
              <span className="text-[#0B1F33] font-bold">78,00 €</span>
              <span className="ml-1 px-1.5 py-0.5 rounded bg-[#D84343] text-white text-[10px] font-extrabold">+16 %</span>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3, duration: 0.35 }}
            className="mt-1.5 pt-1.5 border-t border-[#1769E0]/20 text-[10.5px] font-semibold text-[#24845B] inline-flex items-center gap-1"
          >
            <Check className="w-3 h-3" strokeWidth={3} /> Ajouté au catalogue Produits
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

function StepDeduction() {
  return (
    <motion.div {...stepIn} className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#5E6875] font-bold">Ligne facture · Charcuterie Béarn</div>
      <div className="mt-1.5 font-mono-num text-[12.5px] bg-[#FFF0D2] px-2 py-1 rounded inline-block w-fit font-bold">RIC 45° 70CL X6 — 3 × 78,00 €</div>

      <div className="mt-3 pt-3 border-t border-[#CDD7E3]">
        <div className="text-[10px] uppercase tracking-widest text-[#1769E0] font-bold flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Identifié par RentaScan · Confiance élevée
        </div>
        <div className="mt-1 font-heading text-[20px] font-extrabold text-[#0B1F33] leading-tight">Pastis Ricard 45°</div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
        {[
          ["Catégorie", "Boissons > Spiritueux > Pastis"],
          ["Marque", "Ricard"],
          ["Conditionnement", "Carton de 6 bouteilles"],
          ["Contenance", "70 cl / bouteille"],
          ["Volume total (carton)", "420 cl · 4,2 L"],
          ["Réf. fournisseur", "RIC-45-70-C6"],
        ].map(([k, v]) => (
          <div key={k} className="rounded bg-[#F4F5EF] p-1.5">
            <div className="text-[9px] uppercase tracking-widest font-bold text-[#5E6875]">{k}</div>
            <div className="font-semibold text-[#0B1F33]">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-[#CDD7E3]">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">Conversions déterministes</div>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5 text-[11px]">
          <div className="rounded bg-[#DCEBFF] p-1.5"><div className="text-[9px] text-[#0B1F33]/70 font-bold uppercase tracking-widest">/ bouteille</div><div className="font-mono-num font-bold text-[#0B1F33] text-[12.5px]">13,00 €</div></div>
          <div className="rounded bg-[#DCEBFF] p-1.5"><div className="text-[9px] text-[#0B1F33]/70 font-bold uppercase tracking-widest">/ L</div><div className="font-mono-num font-bold text-[#0B1F33] text-[12.5px]">18,57 €</div></div>
          <div className="rounded bg-[#FFF0D2] p-1.5 border border-[#FFD166]"><div className="text-[9px] text-[#7A4A00] font-bold uppercase tracking-widest">/ cl</div><div className="font-mono-num font-bold text-[#0B1F33] text-[12.5px]">0,1857 €</div></div>
        </div>
      </div>

      <div className="mt-3 pt-3 flex gap-2 shrink-0">
        <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="flex-1 h-10 rounded-lg bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] text-[13px] font-extrabold inline-flex items-center justify-center gap-1.5">
          <Check className="w-4 h-4" /> Confirmer
        </motion.button>
        <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="h-10 px-4 rounded-lg border border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[#0B1F33] text-[13px] font-semibold">
          Modifier
        </motion.button>
      </div>
    </motion.div>
  );
}

function StepCatalog() {
  // Fiche produit editorial "Pastis Ricard 45°" — replaces the previous table view.
  // 12 points on 3 months to draw a truly continuous, organic curve (smoothed with Catmull-Rom).
  // Only the 3 month anchors (Juil./Août/Sept.) carry a label; every other point is a curve node.
  const HIST = [
    { m: "Juil.", v: 74.50, anchor: true  },
    { m: "",      v: 74.65, anchor: false },
    { m: "",      v: 74.90, anchor: false },
    { m: "",      v: 75.20, anchor: false },
    { m: "Août",  v: 75.60, anchor: true  },
    { m: "",      v: 75.90, anchor: false },
    { m: "",      v: 76.55, anchor: false },
    { m: "",      v: 77.30, anchor: false },
    { m: "Sept.", v: 78.00, anchor: true  },
  ];
  const min = Math.min(...HIST.map((h) => h.v));
  const max = Math.max(...HIST.map((h) => h.v));
  const XPAD = 4, YPAD = 10;
  // Point coordinates for the SVG viewBox 0..100.
  const PTS = HIST.map((h, i) => ({
    x: XPAD + (i / (HIST.length - 1)) * (100 - 2 * XPAD),
    y: YPAD + (1 - (h.v - min) / Math.max(0.001, max - min)) * (100 - 2 * YPAD),
  }));
  // Catmull-Rom to cubic bezier — draws a smooth, continuous curve through every point.
  const path = (() => {
    if (PTS.length < 2) return "";
    let d = `M ${PTS[0].x.toFixed(2)} ${PTS[0].y.toFixed(2)}`;
    for (let i = 0; i < PTS.length - 1; i++) {
      const p0 = PTS[i - 1] || PTS[i];
      const p1 = PTS[i];
      const p2 = PTS[i + 1];
      const p3 = PTS[i + 2] || PTS[i + 1];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
    }
    return d;
  })();

  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col"
    >
      {/* Editorial header — deep navy plate with brand */}
      <div className="relative bg-[#0B1F33] text-white px-4 pt-3 pb-10 overflow-hidden">
        <div className="flex items-center justify-between gap-3">
          <span className="font-heading text-[16px] font-extrabold leading-tight text-[#FFD166]">Fiche produit</span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#24E39B]">
            <Check className="w-3 h-3" strokeWidth={3} />
            Ajouté au catalogue
          </span>
        </div>
        <div className="mt-3 flex items-end gap-3">
          {/* Pastis bottle silhouette (inline SVG) */}
          <svg
            viewBox="0 0 48 96"
            className="w-[38px] h-[76px] shrink-0 drop-shadow-[0_10px_20px_rgba(0,0,0,0.35)]"
            aria-hidden
          >
            <rect x="20" y="2" width="8" height="14" fill="#F5C542" />
            <path
              d="M14 20 Q14 16 20 16 L28 16 Q34 16 34 20 L36 30 Q36 34 36 40 L36 88 Q36 94 30 94 L18 94 Q12 94 12 88 L12 40 Q12 34 12 30 Z"
              fill="#F5C542"
            />
            <rect x="15" y="42" width="18" height="30" rx="1" fill="#0B1F33" />
            <text x="24" y="55" textAnchor="middle" fontSize="6" fontWeight="800" fill="#FFD166" fontFamily="Manrope,Arial">RICARD</text>
            <text x="24" y="63" textAnchor="middle" fontSize="4" fontWeight="700" fill="#F5C542" fontFamily="Manrope,Arial">45°</text>
            <text x="24" y="69" textAnchor="middle" fontSize="3" fontWeight="600" fill="#F5C542" fontFamily="Manrope,Arial" opacity="0.8">70 cl</text>
            <rect x="20" y="2" width="4" height="14" fill="#FFD166" opacity="0.7" />
          </svg>

          <div className="flex-1 min-w-0">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-white/60">Ricard · Spiritueux · Pastis</div>
            <div className="mt-0.5 font-heading text-[19px] leading-tight font-extrabold text-white">Pastis Ricard 45°</div>
            <div className="mt-0.5 text-[10.5px] text-white/55">
              Alias : <span className="font-mono-num text-white/80">RIC 45° 70CL X6</span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {["Carton × 6", "70 cl", "45°", "4,2 L"].map((c) => (
                <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/10 border border-white/15 text-[9px] font-semibold text-white/85">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content body */}
      <div className="p-4 flex-1 flex flex-col -mt-7">
        {/* Price grid — 3 cards floating over the header edge */}
        <div className="grid grid-cols-3 gap-1.5">
          <PriceTile label="Prix carton" main="78,00 €" sub="Sept. 26" tone="dark" />
          <PriceTile label="Prix / L" main="18,57 €" sub="Recettes" tone="light" />
          <PriceTile label="Prix / cl" main="0,1857 €" sub="Dose plat" tone="orange" />
        </div>

        {/* Mini price history sparkline — hauteur généreuse pour que la courbe ET les 3 labels de mois soient TOUJOURS visibles en entier, quel que soit le viewport. */}
        <div className="mt-3 rounded-md border border-[#CDD7E3] p-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">Historique · 3 mois</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#D84343]">
              <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-[#D84343]" />
              +4,7 % en sept.
            </span>
          </div>
          {/* Historique — composant partagé ProductPriceChart (segments
              droits Juil→Août→Sept, tracé bleu profond, points blanc/orange).
              Le tracé est TOUJOURS visible ; seule l'opacité globale est
              animée pour éviter que l'utilisateur voie une carte vide. */}
          <div className="mt-2">
            <ProductPriceChart
              points={[
                { label: "Juil.", value: 74.50 },
                { label: "Août",  value: 75.60 },
                { label: "Sept.", value: 78.00 },
              ]}
              heightClassName="h-[168px]"
              valueSuffix="€"
              testId="landing-price-chart"
            />
          </div>
          {/* Labels below the chart — only the 3 anchor months */}
          <div className="mt-1.5 grid grid-cols-3 gap-1 text-center">
            {HIST.filter((h) => h.anchor).map((h, i, arr) => (
              <div key={h.m}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#5E6875]">{h.m}</div>
                <div className={`font-mono-num tabular-nums text-[12px] font-bold ${i === arr.length - 1 ? "text-[#D84343]" : "text-[#0B1F33]"}`}>
                  {h.v.toFixed(2).replace(".", ",")} €
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recipes impacted */}
        <div className="mt-3 space-y-1 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">Recettes utilisant ce produit</div>
          {[
            ["Ricard au verre", "2 cl / verre", "0,371 €"],
            ["Mauresque", "2 cl / verre", "0,371 €"],
          ].map(([name, dose, cost], i) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              className="rounded border border-[#CDD7E3] bg-[#F4F5EF] p-2 flex items-center justify-between text-[12px]"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <ChefHat className="w-3.5 h-3.5 text-[#1769E0] shrink-0" />
                <span className="font-semibold text-[#0B1F33] truncate">{name}</span>
                <span className="text-[#5E6875] font-mono-num text-[11px]">{dose}</span>
              </div>
              <span className="font-bold text-[#D84343] font-mono-num">
                +{cost}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Source footer */}
        <div className="mt-3 rounded bg-[#DCEBFF] p-2 flex items-center justify-between text-[11px] shrink-0">
          <span className="text-[#0B1F33]">Source · dernière facture</span>
          <span className="font-mono-num font-bold text-[#1769E0]">FA-2026-0138 · Charcuterie Béarn</span>
        </div>
      </div>
    </motion.div>
  );
}

/* Small tile used inside StepCatalog price grid */
function PriceTile({ label, main, sub, tone = "light" }) {
  const styles = {
    dark:   "bg-[#0B1F33] text-white border-[#0B1F33]",
    light:  "bg-white text-[#0B1F33] border-[#CDD7E3]",
    orange: "bg-[#FFF0D2] text-[#0B1F33] border-[#FFD166]",
  }[tone];
  const labelColor = tone === "dark" ? "text-white/60" : "text-[#5E6875]";
  const subColor = tone === "dark" ? "text-white/50" : "text-[#5E6875]";
  return (
    <div className={`rounded-md border p-2 shadow-[0_6px_18px_rgba(11,31,51,0.10)] ${styles}`}>
      <div className={`text-[9px] uppercase tracking-widest font-bold ${labelColor}`}>{label}</div>
      <div className="mt-0.5 font-heading text-[17px] font-extrabold tabular-nums leading-none">{main}</div>
      <div className={`mt-1 text-[10px] ${subColor}`}>{sub}</div>
    </div>
  );
}

/* --- Step 4 · Tableau de bord animé : produits en hausse / baisse (remplace l'ancien StepImpact) --- */
function StepDashboard() {
  // Colored monogram used as a "petite photo" of each product (no image assets required).
  const Glyph = ({ color, label }) => (
    <div
      className="w-8 h-8 rounded-md flex items-center justify-center text-white text-[11px] font-extrabold font-heading tracking-tight shrink-0 shadow-[0_4px_10px_rgba(11,31,51,0.20)]"
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {label}
    </div>
  );

  const UP = [
    { name: "Pastis Ricard 45°", old: 67.20, cur: 78.00, pct: 16.0, unit: "€/carton", date: "09/09/26", g: "R",  gc: "#F5C542" },
    { name: "Coppa AOP",         old: 16.00, cur: 17.98, pct: 12.4, unit: "€/kg",     date: "09/09/26", g: "C",  gc: "#8C2E2E" },
    { name: "Jambon cru",        old: 22.50, cur: 24.35, pct:  8.2, unit: "€/kg",     date: "05/09/26", g: "J",  gc: "#A15A3A" },
  ];
  const DOWN = [
    { name: "Saucisson sec",     old: 18.00, cur: 17.08, pct: -5.1, unit: "€/kg",     date: "07/09/26", g: "S",  gc: "#6B3A1D" },
    { name: "Citron vert",       old:  4.20, cur:  3.89, pct: -7.4, unit: "€/kg",     date: "02/09/26", g: "L",  gc: "#3A8C3A" },
  ];

  const Row = ({ item, tone, delay }) => {
    const barColor = tone === "up" ? "#D84343" : "#24845B";
    return (
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay, duration: 0.35, ease: EASE }}
        className="px-3 py-2 flex items-center gap-2.5 border-b border-slate-100 last:border-b-0"
      >
        <Glyph color={item.gc} label={item.g} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-[#0B1F33] truncate leading-tight">{item.name}</div>
          <div className="text-[10px] text-[#5E6875] font-mono-num tabular-nums">
            {item.old.toFixed(2)} → <span className="text-[#0B1F33] font-bold">{item.cur.toFixed(2)}</span> {item.unit}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[13px] font-mono-num font-extrabold tabular-nums tracking-tight" style={{ color: barColor }}>
            {tone === "up" ? "▲" : "▼"} {item.pct > 0 ? "+" : ""}{item.pct.toFixed(1)} %
          </div>
          <div className="text-[9px] text-[#5E6875] font-mono-num">{item.date}</div>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col"
    >
      {/* Header — navy plate */}
      <div className="bg-[#0B1F33] text-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#FFD166]">Tableau de bord</div>
          <div className="font-heading text-[16px] font-extrabold leading-tight">Aujourd'hui</div>
        </div>
        <div className="text-right text-[9.5px] text-white/60">
          <div className="uppercase tracking-widest font-bold">Dernière facture</div>
          <div className="font-mono-num text-white/85 font-semibold">FA-2026-0138 · 09/09</div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-1.5 px-3 pt-3">
        <KpiTile label="Produits suivis" value={42} tone="dark" delay={0.15} />
        <KpiTile label="En hausse"        value={3}  tone="up"   delay={0.30} />
        <KpiTile label="En baisse"        value={2}  tone="down" delay={0.45} />
      </div>

      {/* Hausses */}
      <div className="mt-3 mx-3 rounded-md border border-[#F5C7C7] overflow-hidden">
        <div className="px-3 py-1.5 bg-[#FDECEC] text-[#7C2A21] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-extrabold inline-flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" strokeWidth={3} /> Produits en hausse
          </span>
          <span className="text-[11px] font-mono-num font-extrabold tabular-nums">3</span>
        </div>
        <div className="bg-white">
          {UP.map((it, i) => <Row key={it.name} item={it} tone="up" delay={0.6 + i * 0.15} />)}
        </div>
      </div>

      {/* Baisses */}
      <div className="mt-2 mx-3 rounded-md border border-[#C0E1CC] overflow-hidden mb-3">
        <div className="px-3 py-1.5 bg-[#E4F4EA] text-[#1B5C39] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-extrabold inline-flex items-center gap-1.5">
            <TrendingDown className="w-3 h-3" strokeWidth={3} /> Produits en baisse
          </span>
          <span className="text-[11px] font-mono-num font-extrabold tabular-nums">2</span>
        </div>
        <div className="bg-white">
          {DOWN.map((it, i) => <Row key={it.name} item={it} tone="down" delay={1.1 + i * 0.15} />)}
        </div>
      </div>
    </motion.div>
  );
}

/* --- Step 3 bis · Impact sur les recettes ---
 * Feb 2026 §« recettes à optimiser / excédent de marge » — Panneau
 * jumelé au Tableau de bord dans le module 03. Reprend les prix du
 * dashboard (Ricard +16 %, saucisson -5,1 %) et les convertit en
 * marge à la portion via des hypothèses de démonstration centralisées
 * dans RECIPE_DEMO. Aucune donnée n'est inventée sans hypothèse
 * explicite ; aucun coût ni pourcentage n'est arbitraire. */
const RECIPE_DEMO = {
  ricard: {
    sellPriceHt: 4.00,            // prix de vente au verre (identique au reste de la page)
    costIncreasePerGlass: 0.371,  // hausse démo du coût matière par verre (rouge)
    caption: "Le prix du Ricard augmente. À prix de vente identique, votre marge par verre diminue.",
  },
  planche: {
    // 4 produits à parts égales (démo). Prix de vente et gain de marge
    // affichés tels quels dans la carte « Excédent de marge ».
    portion_g: 25,
    sellPriceHt: 19.00,
    marginGainEur: 0.23,
    caption: "La baisse du saucisson contribue à votre rentabilité. Cette recette dépasse votre objectif de marge.",
  },
};

function StepRecipesImpact() {
  const r = RECIPE_DEMO.ricard;
  const p = RECIPE_DEMO.planche;
  const eur = (v, d = 3) => v.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d, signDisplay: v < 0 ? "always" : "auto" }) + " €";
  const eur2 = (v) => v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col"
    >
      {/* Header — navy plate */}
      <div className="bg-[#0B1F33] text-white px-4 py-3">
        <div className="font-heading text-[16px] font-extrabold leading-tight text-[#FFD166]">Impact sur vos recettes</div>
      </div>

      {/* Card 1 · Ricard au verre — À optimiser */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4, ease: EASE }}
        className="mx-3 mt-3 rounded-md border border-[#F5C7C7] overflow-hidden"
      >
        <div className="px-3 py-1.5 bg-[#FDECEC] text-[#7C2A21] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-extrabold inline-flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" strokeWidth={3} /> À optimiser
          </span>
          <span className="text-[10px] font-mono-num font-extrabold tabular-nums">Ricard au verre</span>
        </div>
        <div className="bg-white px-3 py-2 space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-[#5E6875]">Prix de vente</span>
            <span className="text-[12px] font-mono-num font-bold tabular-nums text-[#0B1F33]">{eur2(r.sellPriceHt)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-[#5E6875]">Coût matière · variation</span>
            <span className="text-[12px] font-mono-num font-extrabold tabular-nums text-[#D84343] inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" strokeWidth={3} /> +16&nbsp;%
            </span>
          </div>
          <div className="rounded bg-[#FDECEC] border border-[#F5C7C7] px-2 py-1 mt-0.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest font-extrabold text-[#7C2A21]">Marge / verre</span>
            <span className="text-[12px] font-mono-num font-extrabold tabular-nums text-[#D84343]">−{eur(r.costIncreasePerGlass)}</span>
          </div>
          <div className="text-[10.5px] leading-tight text-[#5E6875] pt-1">{r.caption}</div>
          <motion.button
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.3 }}
            className="mt-2 h-8 px-3 rounded-md bg-[#FF9F1C] text-[#0B1F33] text-[11px] font-extrabold inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-3 h-3" /> Modifier la recette
          </motion.button>
        </div>
      </motion.div>

      {/* Card 2 · Planche de charcuterie — Excédent de marge */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75, duration: 0.4, ease: EASE }}
        className="mx-3 mt-2 mb-3 rounded-md border border-[#C0E1CC] overflow-hidden"
      >
        <div className="px-3 py-1.5 bg-[#E4F4EA] text-[#1B5C39] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-extrabold inline-flex items-center gap-1.5">
            <TrendingDown className="w-3 h-3" strokeWidth={3} /> Excédent de marge
          </span>
          <span className="text-[10px] font-mono-num font-extrabold tabular-nums">Planche de charcuterie</span>
        </div>
        <div className="bg-white px-3 py-2 space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-[#5E6875]">Prix de vente</span>
            <span className="text-[12px] font-mono-num font-bold tabular-nums text-[#0B1F33]">{eur2(p.sellPriceHt)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-[#5E6875]">Saucisson · variation prix</span>
            <span className="text-[12px] font-mono-num font-extrabold tabular-nums text-[#24845B] inline-flex items-center gap-1">
              <TrendingDown className="w-3 h-3" strokeWidth={3} /> −5,1 %
            </span>
          </div>
          <div className="rounded bg-[#E4F4EA] border border-[#C0E1CC] px-2 py-1 mt-0.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest font-extrabold text-[#1B5C39]">Marge / planche</span>
            <span className="text-[13px] font-mono-num font-extrabold tabular-nums text-[#24845B]">+{eur2(p.marginGainEur)}</span>
          </div>
          <div className="text-[10.5px] leading-tight text-[#5E6875] pt-1">{p.caption}</div>
          <motion.button
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1, duration: 0.3 }}
            className="mt-2 h-8 px-3 rounded-md bg-[#FF9F1C] text-[#0B1F33] text-[11px] font-extrabold inline-flex items-center gap-1.5"
          >
            <TrendingDown className="w-3 h-3" /> Modifier la recette
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* --- Step 5 · Fiche recette · Planche de charcuterie ---
 * Feb 2026 §« fiche recette éditoriale » — Mockup de la même fiche
 * que celle du catalogue : prix de vente, ingrédients avec grammage
 * et prix/kg, coût matière total et marge. Grammages centralisés
 * dans RECIPE_CARD_DEMO (aucun coût inventé sans hypothèse). */
const RECIPE_CARD_DEMO = {
  sellPriceHt: 19.00,
  ingredients: [
    { name: "Coppa",         g: 40, pricePerKg: 17.98 },
    { name: "Jambon cru",    g: 40, pricePerKg: 24.35 },
    { name: "Lonzo",         g: 40, pricePerKg: 22.00 },
    { name: "Saucisson sec", g: 40, pricePerKg: 17.08 },
  ],
};

function StepRecipeCard() {
  const R = RECIPE_CARD_DEMO;
  const eur = (v, d = 2) => v.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d }) + " €";
  const lineCosts = R.ingredients.map((i) => ({ ...i, cost: (i.g * i.pricePerKg) / 1000 }));
  // Feb 2026 §« Fiche recette animée » — Dose vivante sur le dernier
  // ingrédient (saucisson) : simule un ajustement de portion et fait
  // vivre le coût de la ligne, le coût matière total et la marge en
  // temps réel — exactement comme dans l'éditeur de recette de l'app.
  const lastIdx = R.ingredients.length - 1;
  const dyn = R.ingredients[lastIdx];
  const baseCostOthers = lineCosts.slice(0, lastIdx).reduce((s, i) => s + i.cost, 0);

  const doseMV = useMotionValue(dyn.g);
  const [doseG, setDoseG] = useState(dyn.g);
  useEffect(() => {
    const ctrl = animate(doseMV, [dyn.g, dyn.g + 15, dyn.g], {
      duration: 7, repeat: Infinity, ease: "easeInOut", times: [0, 0.5, 1], repeatDelay: 0.4,
    });
    return () => ctrl.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useMotionValueEvent(doseMV, "change", (v) => setDoseG(v));
  const doseCost  = (doseG * dyn.pricePerKg) / 1000;
  const totalCost = baseCostOthers + doseCost;
  const margin    = R.sellPriceHt - totalCost;
  const doseText     = `${Math.round(doseG)} g`;
  const doseCostText = eur(doseCost, 3);
  const totalCostTxt = eur(totalCost);
  const marginTxt    = eur(margin);
  const marginPctTxt = `${((margin / R.sellPriceHt) * 100).toFixed(1)} %`;

  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col"
    >
      {/* Header navy */}
      <div className="bg-[#0B1F33] text-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#FFD166]">Fiche recette</div>
          <div className="font-heading text-[16px] font-extrabold leading-tight">Planche de charcuterie</div>
        </div>
        <div className="text-right">
          <div className="text-[9.5px] uppercase tracking-widest font-bold text-white/60">Prix de vente</div>
          <div className="font-mono-num text-[15px] font-extrabold tabular-nums text-[#FFD166]">{eur(R.sellPriceHt)}</div>
        </div>
      </div>

      {/* Ingredients block */}
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <div className="text-[9.5px] uppercase tracking-widest font-bold text-[#5E6875]">Ingrédients</div>
        <div className="text-[9.5px] uppercase tracking-widest font-bold text-[#FF9F1C] inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" strokeWidth={2.6} />
          Dose en direct
        </div>
      </div>
      <div className="px-4 pb-3 space-y-1.5">
        {lineCosts.map((it, k) => {
          const isDyn = k === lastIdx;
          return (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 + k * 0.09, duration: 0.35, ease: EASE }}
              className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-x-3 py-1.5 border-b border-[#EEF1F5] last:border-b-0"
            >
              <span className="w-7 h-7 rounded-md bg-[#FDECEC] text-[#D84343] inline-flex items-center justify-center shrink-0">
                <Beef className="w-3.5 h-3.5" strokeWidth={2.2} />
              </span>
              <div className="text-[12.5px] font-semibold text-[#0B1F33] truncate">{it.name}</div>
              {isDyn ? (
                <motion.div className="relative text-[11px] font-mono-num font-extrabold tabular-nums text-[#0B1F33] bg-[#FFF3D9] rounded px-1.5 py-0.5 border border-[#FF9F1C]/50">
                  {doseText}
                  <motion.span
                    aria-hidden
                    className="absolute right-[3px] top-1/2 -translate-y-1/2 w-[2px] h-[10px] bg-[#FF9F1C]"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                  />
                </motion.div>
              ) : (
                <div className="text-[11px] text-[#5E6875] font-mono-num tabular-nums">{it.g} g</div>
              )}
              <div className="text-[11px] text-[#5E6875] font-mono-num tabular-nums">{eur(it.pricePerKg)}/kg</div>
              {isDyn ? (
                <motion.div className="text-[12px] font-mono-num font-extrabold tabular-nums text-[#0B1F33] min-w-[52px] text-right">
                  {doseCostText}
                </motion.div>
              ) : (
                <div className="text-[12px] font-mono-num font-extrabold tabular-nums text-[#0B1F33]">{eur(it.cost, 3)}</div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Totals */}
      <div className="mt-auto px-4 pb-4 space-y-1.5">
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.35, ease: EASE }}
          className="flex items-baseline justify-between rounded-md bg-[#F4F5EF] px-3 py-2"
        >
          <span className="text-[10.5px] uppercase tracking-widest font-extrabold text-[#5E6875]">Coût matière</span>
          <motion.span className="text-[14px] font-mono-num font-extrabold tabular-nums text-[#0B1F33] min-w-[70px] text-right">
            {totalCostTxt}
          </motion.span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: [0.96, 1.04, 1] }}
          transition={{ delay: 0.95, duration: 0.65, ease: EASE, times: [0, 0.6, 1] }}
          className="flex items-baseline justify-between rounded-md bg-[#E4F4EA] border border-[#C0E1CC] px-3 py-2 shadow-[0_6px_18px_rgba(36,132,91,0.12)]"
        >
          <div className="text-[10.5px] uppercase tracking-widest font-extrabold text-[#1B5C39] flex items-baseline gap-1">
            Marge · <motion.span className="font-mono-num tabular-nums">{marginPctTxt}</motion.span>
          </div>
          <motion.span className="text-[15px] font-mono-num font-extrabold tabular-nums text-[#24845B] min-w-[70px] text-right">
            {marginTxt}
          </motion.span>
        </motion.div>
      </div>
    </motion.div>
  );
}

/* --- Step 5 (module 07) · Fournisseurs & Négociation · véritable dossier Intégral --- */
function StepSuppliers() {
  const ROWS = [
    { name: "Pastis Ricard 45°",   old: 67.20, cur: 78.00, pct: 16.0, unit: "€/carton", saving: 486 },
    { name: "Coppa AOP",           old: 16.00, cur: 17.98, pct: 12.4, unit: "€/kg",     saving: 320 },
    { name: "Jambon cru affiné",   old: 22.50, cur: 24.35, pct:  8.2, unit: "€/kg",     saving: 180 },
  ];
  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 rounded-[6px] bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[#CDD7E3] flex flex-col"
    >
      <div className="bg-[#0B1F33] text-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#FFD166]">Dossier de négociation</div>
          <div className="font-heading text-[16px] font-extrabold leading-tight">Maison des Salaisons</div>
        </div>
      </div>
      <div className="px-3 pt-3">
        <div className="text-[9.5px] uppercase tracking-widest font-bold text-[#5E6875]">Levier fournisseur</div>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          <div className="rounded border border-[#0B1F33]/12 bg-[#FFF7E5] p-2">
            <div className="text-[8.5px] uppercase tracking-widest font-bold text-[#5E6875]">Volume / an</div>
            <div className="mt-0.5 font-heading text-[14px] font-extrabold text-[#0B1F33] tabular-nums">12 480 €</div>
          </div>
          <div className="rounded border border-[#0B1F33]/12 bg-white p-2">
            <div className="text-[8.5px] uppercase tracking-widest font-bold text-[#5E6875]">Références</div>
            <div className="mt-0.5 font-heading text-[14px] font-extrabold text-[#0B1F33] tabular-nums">7</div>
          </div>
          <div className="rounded border border-[#F5C7C7] bg-[#FDECEC] p-2">
            <div className="text-[8.5px] uppercase tracking-widest font-bold text-[#7C2A21]">Hausses</div>
            <div className="mt-0.5 font-heading text-[14px] font-extrabold text-[#D84343] tabular-nums">3</div>
          </div>
        </div>
      </div>
      <div className="mx-3 mt-3 rounded-md border border-[#CDD7E3] overflow-hidden">
        <div className="px-3 py-1.5 bg-[#F4F5EF] text-[#0B1F33] text-[9.5px] uppercase tracking-widest font-extrabold flex items-center justify-between">
          <span>Produits concernés</span>
          <span className="text-[10.5px] font-mono-num tabular-nums text-[#D84343] font-extrabold">Perte potentielle · 986 €/an</span>
        </div>
        {ROWS.map((r, i) => (
          <motion.div key={r.name} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.15, duration: 0.35, ease: EASE }} className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-2.5">
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-[#0B1F33] truncate leading-tight">{r.name}</div>
              <div className="text-[10px] text-[#5E6875] font-mono-num tabular-nums">{r.old.toFixed(2)} → <span className="text-[#0B1F33] font-bold">{r.cur.toFixed(2)}</span> {r.unit}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[12px] font-mono-num font-extrabold text-[#D84343] tabular-nums">+{r.pct.toFixed(1)} %</div>
              <div className="text-[10px] font-mono-num text-[#24845B] font-bold">{r.saving} €/an</div>
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.4, ease: EASE }} className="mx-3 mt-3 mb-3 rounded-md border border-[#DCEBFF] bg-[#F5FAFF] p-3">
        <div className="text-[9.5px] uppercase tracking-widest font-bold text-[#1769E0] flex items-center gap-1.5"><Mail className="w-3 h-3" /> Brouillon personnalisé</div>
        <div className="mt-1.5 text-[11px] leading-relaxed text-[#0B1F33]">
          « Bonjour, notre volume annuel chez vous représente <b>12 480 €</b>. Nous relevons <b>3 hausses</b> simultanées (+16 %, +12 %, +8 %). Nous souhaiterions revenir aux prix de référence pour maintenir notre engagement volume… »
        </div>
        <motion.button initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.3, duration: 0.3 }} className="mt-2 h-9 px-3 rounded-md bg-[#FF9F1C] text-[#0B1F33] text-[11px] font-extrabold inline-flex items-center gap-1.5 text-left leading-tight">
          <Copy className="w-3 h-3" /> Ouvrir le dossier · 986 € de gain potentiel/an
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* --- Step 5 (module 05) · Éditeur de menu & QR code · maquette animée pure HTML/Framer-Motion ---
 * Feb 2026 v7 — Remplace le fichier vidéo par une simulation React
 * synchronisée (8 s en boucle, 60 fps). Élimine toute latence de
 * décodage vidéo, poster ou buffering, et évite d'incruster le nom
 * "Di Carlo" (rendu dynamique côté HTML → nom d'établissement de la
 * démo = "Le petit bistrot"). Respecte `prefers-reduced-motion` : la
 * boucle se fige sur la dernière étape utile (menu complet + QR).
 */
function StepMenuEditor() {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState(0); // 0..6

  // Boucle 8 s : 7 phases discrètes de ~1150 ms, framer-motion assure
  // les interpolations continues entre chaque phase (opacity/y/scale).
  useEffect(() => {
    if (reduce) { setPhase(5); return; }
    let p = 0;
    const t = setInterval(() => {
      p = (p + 1) % 7;
      setPhase(p);
    }, 1150);
    return () => clearInterval(t);
  }, [reduce]);

  const photosOn        = phase >= 2;
  const descriptionsOn  = phase >= 3;
  const playfairOn      = phase >= 1;
  const qrOpen          = phase >= 5;

  // Recettes affichées dans l'aperçu (subset qui tient dans 3:4)
  const DISHES = [
    { name: "Salade César au poulet", price: "14,00 €",
      desc: "Romaine croquante, poulet fermier grillé, parmesan affiné et croûtons dorés.",
      photo: "/api/demo-photos/salade-cesar-poulet.jpg" },
    { name: "Soupe à l'oignon gratinée", price: "9,00 €",
      desc: "Oignons jaunes fondants au vin rouge, croûtons rustiques et emmental gratiné.",
      photo: "/api/demo-photos/soupe-oignon-gratinee.jpg" },
    { name: "Planche de charcuterie", price: "14,00 €",
      desc: "Coppa AOP, lonzo de Corse, saucisson artisanal et jambon cru longuement affiné.",
      photo: "/api/demo-photos/planche-charcuterie.jpg" },
  ];

  return (
    <motion.div
      {...stepIn}
      className="absolute inset-0 flex items-center justify-center"
      data-testid="landing-menu-editor-video"
    >
      <div className="relative w-full max-w-full max-h-full aspect-[3/4] rounded-[6px] overflow-hidden bg-white shadow-[0_30px_80px_rgba(11,31,51,0.25)] border border-[#CDD7E3]"
           style={{ maxHeight: "100%" }}>
        {/* -------- Barre app -------- */}
        <div className="h-8 bg-white border-b border-[#EEF2F7] flex items-center justify-between px-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm bg-[#0B1F33]" />
            <span className="text-[10.5px] font-extrabold text-[#0B1F33]">Le petit bistrot</span>
          </div>
          <motion.span
            key={`saved-${phase}`}
            initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="inline-flex items-center gap-1 text-[9px] px-1.5 py-[1px] rounded-full bg-[#E4F4EA] text-[#1B5C39] font-bold"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B5C39]" /> Analyse terminée · 42 produits
          </motion.span>
        </div>

        {/* -------- Header page « Menu » -------- */}
        <div className="px-4 pt-3">
          <div className="text-[8.5px] uppercase tracking-widest font-bold text-[#5E6875]">Éditeur & QR</div>
          <div className="mt-0.5 font-heading text-[18px] font-extrabold text-[#0B1F33] leading-tight">Menu</div>
          <div className="text-[9px] text-[#5E6875] mt-0.5">Personnalisez, ajoutez photos, traductions, QR code et impression.</div>
          {/* Actions row */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <motion.span
              animate={{ backgroundColor: playfairOn ? "#FF9F1C" : "#F4F5EF" }}
              transition={{ duration: 0.4, ease: EASE }}
              className="inline-flex items-center gap-1 px-2 h-6 rounded-md text-[9px] font-extrabold text-[#0B1F33]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F33]" /> Enregistré
            </motion.span>
            {["Aperçu client", "QR Menu", "Imprimer / PDF"].map((lbl, i) => {
              const isQr = lbl === "QR Menu";
              const active = isQr && phase === 4;
              return (
                <motion.span
                  key={lbl}
                  animate={active ? { scale: [1, 1.06, 1], boxShadow: ["0 0 0 rgba(23,105,224,0)", "0 0 0 3px rgba(23,105,224,0.25)", "0 0 0 rgba(23,105,224,0)"] } : {}}
                  transition={{ duration: 0.8, ease: EASE }}
                  className={`inline-flex items-center px-2 h-6 rounded-md border border-[#CDD7E3] text-[9px] font-bold ${isQr && qrOpen ? "bg-[#0B1F33] text-white border-[#0B1F33]" : "bg-white text-[#0B1F33]"}`}
                >
                  {lbl}
                </motion.span>
              );
            })}
          </div>
        </div>

        {/* -------- Card personnalisation -------- */}
        <div className="mx-4 mt-3 rounded-md border border-[#EEF2F7] bg-white p-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-extrabold text-[#0B1F33]">✦ Personnalisation visuelle</span>
            <span className="inline-flex gap-1">
              <span className="w-4 h-4 rounded-sm border border-[#CDD7E3]" />
              <span className="w-4 h-4 rounded-sm border border-[#CDD7E3]" />
              <span className="w-4 h-4 rounded-sm bg-[#0B1F33]" />
              <span className="w-4 h-4 rounded-sm border border-[#CDD7E3]" />
            </span>
          </div>
          <div className="mt-2 text-[8px] uppercase tracking-widest font-bold text-[#5E6875]">1 · Polices</div>
          <div className="mt-1 grid grid-cols-2 gap-1.5">
            <div>
              <div className="text-[7.5px] text-[#5E6875]">Titres</div>
              <motion.div
                animate={{ borderColor: phase === 1 ? "#1769E0" : "#CDD7E3", backgroundColor: phase === 1 ? "#EEF4FF" : "#FFFFFF" }}
                transition={{ duration: 0.3, ease: EASE }}
                className="mt-0.5 h-6 px-1.5 rounded border flex items-center justify-between"
              >
                <span
                  className="text-[9.5px] text-[#0B1F33]"
                  style={{ fontFamily: playfairOn ? '"Playfair Display", serif' : "Inter, sans-serif", fontWeight: playfairOn ? 600 : 400 }}
                >
                  {playfairOn ? "Playfair Display" : "Inter"}
                </span>
                <ChevronRight className="w-2.5 h-2.5 rotate-90 text-[#5E6875]" />
              </motion.div>
            </div>
            {[["Noms de plats", "Inter"], ["Descriptions", "Inter"], ["Prix", "Inter"]].map(([lbl, v]) => (
              <div key={lbl}>
                <div className="text-[7.5px] text-[#5E6875]">{lbl}</div>
                <div className="mt-0.5 h-6 px-1.5 rounded border border-[#CDD7E3] flex items-center justify-between">
                  <span className="text-[9.5px] text-[#0B1F33]">{v}</span>
                  <ChevronRight className="w-2.5 h-2.5 rotate-90 text-[#5E6875]" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[8px] uppercase tracking-widest font-bold text-[#5E6875]">2 · Affichage</div>
          <div className="mt-1 flex gap-1.5">
            {[
              { lbl: "Afficher les photos",       on: photosOn,        active: phase === 2 },
              { lbl: "Afficher les descriptions", on: descriptionsOn,  active: phase === 3 },
            ].map((t) => (
              <motion.span
                key={t.lbl}
                animate={{ backgroundColor: t.on ? "#0B1F33" : "#EEF4FF", color: t.on ? "#FFFFFF" : "#0B1F33",
                  scale: t.active ? [1, 1.05, 1] : 1 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="inline-flex items-center px-1.5 h-5 rounded text-[8.5px] font-bold"
              >
                {t.on && <Check className="w-2 h-2 mr-1" strokeWidth={3} />}
                {t.lbl}
              </motion.span>
            ))}
          </div>
        </div>

        {/* -------- Aperçu client -------- */}
        <div className="mx-4 mt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[8px] uppercase tracking-widest font-bold text-[#5E6875]">Aperçu client · Ordinateur</span>
            <span className="text-[8px] text-[#1769E0] font-bold">Rafraîchir</span>
          </div>
          <div className="mt-1 rounded-md border border-[#EEF2F7] bg-[#FAFAF7] p-2">
            <div className="text-[7px] uppercase tracking-widest font-bold text-[#5E6875]">Menu du jour</div>
            <motion.div
              key={`title-${playfairOn}`}
              initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="mt-0.5 text-center font-extrabold text-[#0B1F33]"
              style={{
                fontFamily: playfairOn ? '"Playfair Display", serif' : "Inter, sans-serif",
                fontSize: playfairOn ? 22 : 18,
                letterSpacing: playfairOn ? "-0.01em" : "-0.02em",
              }}
            >
              Le petit bistrot
            </motion.div>
            <div className="mt-2 space-y-1.5">
              {DISHES.map((d, i) => (
                <div key={d.name} className="flex items-start gap-2">
                  {/* Photo apparaît en cascade quand photosOn */}
                  <motion.div
                    animate={{
                      opacity: photosOn ? 1 : 0,
                      width: photosOn ? 34 : 0,
                      marginRight: photosOn ? 0 : 0,
                    }}
                    transition={{ duration: 0.45, delay: photosOn ? i * 0.09 : 0, ease: EASE }}
                    className="h-9 rounded overflow-hidden bg-[#EEF2F7] shrink-0"
                    style={{ width: 34 }}
                  >
                    <img src={d.photo} alt="" className="w-full h-full object-cover" />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[9.5px] font-extrabold text-[#0B1F33] truncate">{d.name}</span>
                      <span className="text-[9.5px] font-mono-num tabular-nums text-[#0B1F33]">{d.price}</span>
                    </div>
                    <motion.div
                      animate={{
                        opacity: descriptionsOn ? 1 : 0,
                        height: descriptionsOn ? "auto" : 0,
                      }}
                      transition={{ duration: 0.4, delay: descriptionsOn ? i * 0.09 : 0, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="text-[7.5px] text-[#5E6875] leading-tight pt-0.5">{d.desc}</div>
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* -------- Overlay QR (phases 5-6) -------- */}
        <AnimatePresence>
          {qrOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 bg-[#0B1F33]/50 backdrop-blur-[2px] flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0.9, y: 8, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="w-[64%] rounded-md bg-white p-3 text-center shadow-[0_20px_50px_rgba(11,31,51,0.35)]"
              >
                <div className="text-[8.5px] uppercase tracking-widest font-bold text-[#5E6875]">QR Menu public</div>
                <div className="mt-1 text-[12px] font-extrabold text-[#0B1F33]">Le petit bistrot</div>
                {/* SVG QR stylisé (motif damier généré, purement décoratif) */}
                <div className="mt-2 mx-auto w-[72%] aspect-square bg-white border border-[#0B1F33] p-1 rounded-sm">
                  <svg viewBox="0 0 21 21" className="w-full h-full" shapeRendering="crispEdges" aria-hidden>
                    {Array.from({ length: 21 * 21 }).map((_, i) => {
                      const x = i % 21, y = Math.floor(i / 21);
                      const corner = (x < 7 && y < 7) || (x > 13 && y < 7) || (x < 7 && y > 13);
                      const cornerFill = corner && (
                        (x === 0 || x === 6 || (x === 20 - 0) || (x === 20 - 6) || y === 0 || y === 6 || y === 20 - 0 || y === 20 - 6) ||
                        (x >= 2 && x <= 4 && y >= 2 && y <= 4) ||
                        (x >= 16 && x <= 18 && y >= 2 && y <= 4) ||
                        (x >= 2 && x <= 4 && y >= 16 && y <= 18)
                      );
                      const noise = ((x * 7 + y * 11 + x * y) % 5) < 2;
                      const on = corner ? cornerFill : noise;
                      if (!on) return null;
                      return <rect key={i} x={x} y={y} width="1" height="1" fill="#0B1F33" />;
                    })}
                  </svg>
                </div>
                <div className="mt-2 text-[8px] font-mono-num text-[#5E6875] truncate">rentascan.app/m/le-petit-bistrot</div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.25, duration: 0.3 }}
                  className="mt-2 inline-flex items-center gap-1 px-2 h-6 rounded-md bg-[#FF9F1C] text-[#0B1F33] text-[9px] font-extrabold"
                >
                  <Sparkles className="w-2.5 h-2.5" /> Menu public actif
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* Small KPI tile used inside StepDashboard */
function KpiTile({ label, value, tone = "dark", delay = 0 }) {
  const cfg = {
    dark: { bg: "bg-[#0B1F33]", txt: "text-white",         lab: "text-white/60" },
    up:   { bg: "bg-[#FDECEC]", txt: "text-[#7C2A21]",     lab: "text-[#7C2A21]/70" },
    down: { bg: "bg-[#E4F4EA]", txt: "text-[#1B5C39]",     lab: "text-[#1B5C39]/70" },
  }[tone] || { bg: "bg-white", txt: "text-[#0B1F33]", lab: "text-[#5E6875]" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: EASE }}
      className={`rounded-md p-2 ${cfg.bg}`}
    >
      <div className={`text-[8.5px] uppercase tracking-widest font-bold ${cfg.lab}`}>{label}</div>
      <div className={`mt-0.5 font-heading text-[22px] leading-none font-extrabold tabular-nums ${cfg.txt}`}>
        <Counter value={value} duration={0.9} resetKey={0} />
      </div>
    </motion.div>
  );
}

/* ============================================================
   FOURNISSEURS
   ============================================================ */
function SuppliersSection() {
  // Gain potentiel = variation vs référence × volume annuel moyen.
  // Ces montants sont calculés côté backend dans /api/recommendations
  // (annual_saving_potential). Ici on garde les mêmes chiffres pour montrer
  // exactement ce que la formule Intégral affichera dans l'app.
  const suppliers = [
    { name: "Coppa AOP", price: "18,56 €/kg", trend: "+16 %", saving: "491 €", w: "65%" },
    { name: "Entrecôte de bœuf", price: "24,00 €/kg", trend: "+14 %", saving: "2 520 €", w: "88%" },
    { name: "Blanc de poulet", price: "0,01 €/g", trend: "+5 %", saving: "180 €", w: "42%" },
  ];
  return (
    <Section id="fournisseurs" className="bg-[#FFF0D2] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="order-2 lg:order-1">
          <img src={PHOTOS.chefsTalk} alt="Discussion entre restaurateurs" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover rounded-2xl shadow-[0_30px_60px_rgba(11,31,51,0.20)]" />
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal><div className="eyebrow">07 · Fournisseurs & négociation</div></Reveal>
          <Reveal as="h2" className="display-2 mt-4">Votre négociation clé en main.</Reveal>
          <StepPoints items={[
            "Fournisseurs détectés et classés lors du scan de facture.",
            "Dossier de négociation généré directement par fournisseur.",
          ]} />
          {/* Feb 2026 §« intégration story 05 » — Le module 05 ·
              Fournisseurs & négociation (StepSuppliers, dossier + email
              brouillon) s'intercale entre le titre et les 3 cartes
              produit. Container hauteur définie pour que le shell
              `absolute inset-0` de StepSuppliers ait un contexte de
              positionnement. */}
          <div className="mt-8 mx-auto lg:mx-0 relative w-full max-w-[420px]" style={{ aspectRatio: "1 / 1.414" }}>
            <StepSuppliers />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ============ ROI BAND — nouvelle promesse ============ */
function RoiBand() {
  // Discreet continuous loop: every 5.8s the illustrative calc replays.
  const [loopKey, setLoopKey] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setLoopKey((k) => k + 1), 5800);
    return () => clearInterval(t);
  }, [reduce]);
  return (
    <section className="relative bg-[#0B1F33] text-white overflow-hidden py-24 lg:py-32">
      <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#1769E0]/25 blur-3xl" />
      <div aria-hidden className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#FF9F1C]/15 blur-3xl" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <div>
          <Reveal><div className="eyebrow-light text-[#FFD166]">Retour sur investissement</div></Reveal>
          <Reveal as="h2" className="display-2 mt-4 text-white">
            Le contrôle de vos coûts<br />peut vous rapporter gros.
          </Reveal>
          <Reveal className="mt-6 text-[16.5px] leading-relaxed text-white/75 max-w-[520px]">
            <p>Une augmentation de seulement <b className="text-white">0,25 €</b> sur un plat vendu <b className="text-white">20 fois par jour</b> représente <b className="text-[#FFD166]">150 € de marge en moins chaque mois</b>.</p>
          </Reveal>
          <Reveal className="mt-4 text-[14.5px] leading-relaxed text-white/70 max-w-[520px]">
            <p>RentaScan vous donne les moyens d'agir avant que la perte ne s'accumule.</p>
          </Reveal>
          <Reveal className="mt-8 flex flex-wrap gap-3">
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link to="/inscription?plan=monthly" className="btn-orange" data-testid="roi-cta">
                Essayez gratuitement <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </Reveal>
          <Reveal className="mt-6 text-[11.5px] text-white/50 max-w-[520px]">
            <p>Exemple illustratif basé sur 20 plats servis par jour pendant 30 jours. Les résultats dépendent de chaque établissement et des décisions appliquées.</p>
          </Reveal>
        </div>

        {/* Right — animated calculation, replayed discreetly on a loop */}
        <Reveal>
          <div key={loopKey} data-testid="roi-loop-calc" className="relative rounded-2xl bg-white/[0.04] backdrop-blur border border-white/12 p-8 lg:p-10">
            <div className="text-[11.5px] uppercase tracking-[0.16em] font-bold text-[#FFD166] flex items-center gap-2">
              Calcul illustratif
              <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-[#FFD166] animate-pulse" />
            </div>
            <div className="mt-4 flex items-baseline gap-3 flex-wrap">
              <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5, ease: EASE }}
                className="font-heading text-[64px] font-extrabold text-white leading-none tabular-nums">0,25 €</motion.span>
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-[14px] text-white/60">de hausse / plat</motion.span>
            </div>
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
              className="mt-4 flex items-center gap-3">
              <span className="text-[18px] font-extrabold text-white/70">×</span>
              <span className="font-heading text-[28px] font-extrabold text-white tabular-nums">20 assiettes</span>
              <span className="text-[13px] text-white/50">/ jour</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}
              className="mt-2 flex items-center gap-3">
              <span className="text-[18px] font-extrabold text-white/70">×</span>
              <span className="font-heading text-[28px] font-extrabold text-white tabular-nums">30 jours</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.05, duration: 0.6, ease: EASE }}
              className="mt-6 pt-6 border-t border-white/15">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-white/60">= Marge perdue / mois</div>
              <div className="mt-1 font-heading text-[80px] leading-none font-extrabold text-[#FF9F1C] tabular-nums">
                <Counter value={150} suffix=" €" duration={1.4} resetKey={loopKey} />
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#FF9F1C]/15 border border-[#FF9F1C]/40 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-widest text-[#FFD166]">
                <span aria-hidden className="w-1 h-1 rounded-full bg-[#FF9F1C]" />
                par plat impacté
              </div>
            </motion.div>
            <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4 text-[13px]">
              <div>
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-white/50">Marge perdue estimée par plat</div>
                <div className="font-heading text-[22px] font-extrabold text-[#D84343] tabular-nums mt-1">150 €/mois</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-white/50">Abonnement</div>
                <div className="font-heading text-[22px] font-extrabold text-white tabular-nums mt-1">à partir de 34,99 € HT/mois</div>
              </div>
            </div>
            <div className="mt-4 text-[12px] text-white/60 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-[#FFD166]" />
              Actions : ajuster la carte ou négocier avec le fournisseur.
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ PRICING ============ */
function Pricing() {
  const [annual, setAnnual] = useState(false);

  const PLANS = [
    {
      key: "essentiel",
      name: "Essentiel",
      monthly: 34.99,
      yearly: 349.90,
      yearlyEquiv: 29.16,
      cta: "Choisir Essentiel",
      features: [
        "10 factures analysées par mois",
        "50 produits",
        "10 fiches recettes",
        "Historique des prix sur 6 mois",
        "Éditeur de menu · QR code · Impression",
      ],
      note: null,
      // Mobile order 2, Desktop col 1
      orderClass: "order-2 lg:order-1",
      highlight: false,
    },
    {
      key: "pro",
      name: "Pro",
      monthly: 49.99,
      yearly: 499.90,
      yearlyEquiv: 41.66,
      cta: "Choisir Pro",
      features: [
        "25 factures analysées par mois",
        "150 produits",
        "25 fiches recettes",
        "Historique des prix sur 12 mois",
        "Négociation personnalisée",
        "Éditeur de menu · QR code · Impression",
      ],
      note: null,
      orderClass: "order-1 lg:order-2",
      highlight: false,
      badge: "Le plus choisi",
    },
    {
      key: "integral",
      name: "Intégral",
      monthly: 69.99,
      yearly: 699.90,
      yearlyEquiv: 58.33,
      cta: "Choisir Intégral",
      features: [
        "Factures analysées illimitées",
        "Produits illimités",
        "Fiches recettes illimitées",
        "Historique des prix illimité",
        "Négociation personnalisée",
        "Éditeur de menu · QR code · Impression",
        "Traduction du menu en 7 langues",
      ],
      note: "Utilisation illimitée dans le cadre d'un usage raisonnable pour un établissement.",
      orderClass: "order-3 lg:order-3",
      highlight: true,
    },
  ];

  const fmtEur = (n) => n.toFixed(2).replace(".", ",");
  const planSlug = (p) => `${p.key}_${annual ? "yearly" : "monthly"}`;

  return (
    <Section id="tarifs" className="bg-[#F4F5EF] py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <Reveal><div className="eyebrow">Tarifs</div></Reveal>
          <Reveal as="h2" className="display-2 mt-3 max-w-2xl mx-auto">
            Une formule adaptée à chaque besoin.
          </Reveal>
          <Reveal className="mt-4 text-[15px] text-[#5E6875] max-w-xl mx-auto">
            <p>Progressez selon votre volume de factures, votre catalogue de produits et vos recettes.</p>
          </Reveal>
        </div>

        {/* Billing toggle */}
        <Reveal className="flex justify-center mb-12">
          <div
            role="tablist"
            aria-label="Fréquence de facturation"
            data-testid="pricing-toggle"
            className="relative inline-flex items-center rounded-full bg-white border border-[#CDD7E3] p-1 shadow-sm"
          >
            <button
              role="tab"
              aria-selected={!annual}
              data-testid="pricing-toggle-monthly"
              onClick={() => setAnnual(false)}
              className={`relative z-10 px-5 h-10 text-[13.5px] font-semibold rounded-full transition-colors ${
                !annual ? "bg-[#0B1F33] text-white" : "text-[#0B1F33] hover:text-[#0B1F33]/80"
              }`}
            >
              Mensuel
            </button>
            <button
              role="tab"
              aria-selected={annual}
              data-testid="pricing-toggle-yearly"
              onClick={() => setAnnual(true)}
              className={`relative z-10 px-5 h-10 text-[13.5px] font-semibold rounded-full inline-flex items-center gap-2 transition-colors ${
                annual ? "bg-[#0B1F33] text-white" : "text-[#0B1F33] hover:text-[#0B1F33]/80"
              }`}
            >
              Annuel
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF9F1C] text-[#0B1F33] text-[10.5px] font-extrabold uppercase tracking-wide">
                2 mois offerts
              </span>
            </button>
          </div>
        </Reveal>

        {/* Plan cards */}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:items-stretch">
          {PLANS.map((p) => {
            const shownPrice = annual ? p.yearlyEquiv : p.monthly;
            return (
              <Reveal
                key={p.key}
                className={`${p.orderClass} flex`}
              >
                <motion.div
                  data-testid={`pricing-card-${p.key}`}
                  animate={p.highlight ? {
                    boxShadow: [
                      "0 20px 60px rgba(255,159,28,0.20), 0 0 0 0 rgba(255,209,102,0.55)",
                      "0 26px 80px rgba(255,159,28,0.32), 0 0 0 8px rgba(255,209,102,0)",
                      "0 20px 60px rgba(255,159,28,0.20), 0 0 0 0 rgba(255,209,102,0.55)",
                    ],
                  } : {}}
                  transition={p.highlight ? { duration: 3, ease: "easeInOut", repeat: Infinity } : {}}
                  className={`relative flex flex-col w-full rounded-lg bg-white p-7 lg:p-8 ${
                    p.highlight
                      ? "border border-[#FF9F1C] lg:scale-[1.03] overflow-hidden pt-12 lg:pt-14 ring-2 ring-[#FFD166]/40 ring-offset-2 ring-offset-[#F4F5EF]"
                      : p.badge
                        ? "border-2 border-[#FF9F1C] shadow-[0_10px_30px_rgba(255,159,28,0.12)]"
                        : "border border-[#CDD7E3] shadow-[0_10px_30px_rgba(11,31,51,0.06)]"
                  }`}
                >
                  {p.highlight && (
                    <>
                      {/* Halo lumineux animé — arrière-plan de la carte */}
                      <motion.span
                        aria-hidden
                        className="pointer-events-none absolute -inset-1 rounded-lg opacity-30 blur-2xl"
                        style={{
                          background:
                            "conic-gradient(from 0deg, rgba(255,159,28,0.28), rgba(255,209,102,0.10), rgba(255,159,28,0.28), rgba(255,209,102,0.10), rgba(255,159,28,0.28))",
                        }}
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 12, ease: "linear", repeat: Infinity }}
                      />
                      {/* Bandeau supérieur premium avec balayage brillant */}
                      <div
                        data-testid="pricing-badge-most-chosen"
                        className="absolute top-0 left-0 right-0 flex items-center justify-center gap-2 h-9 bg-gradient-to-r from-[#F58F06] via-[#FFB84D] to-[#F58F06] text-[#0B1F33] text-[12px] font-extrabold uppercase tracking-[0.14em] overflow-hidden"
                      >
                        <Sparkles className="w-3.5 h-3.5 relative z-10" strokeWidth={2.8} />
                        <span className="relative z-10">Meilleure offre</span>
                        {/* Sweep shimmer — traverse le bandeau régulièrement */}
                        <motion.span
                          aria-hidden
                          className="absolute inset-y-0 w-1/3"
                          style={{
                            background:
                              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)",
                          }}
                          initial={{ x: "-120%" }}
                          animate={{ x: "220%" }}
                          transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.8 }}
                        />
                      </div>
                    </>
                  )}
                  {!p.highlight && p.badge && (
                    <div
                      data-testid={`pricing-badge-${p.key}`}
                      className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-[#FF9F1C] text-[#0B1F33] text-[11px] font-extrabold uppercase tracking-wide shadow-md whitespace-nowrap"
                    >
                      {p.badge}
                    </div>
                  )}

                  <div className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-[#5E6875]">
                    {p.name}
                  </div>

                  {/* Price */}
                  <div className="mt-3 flex items-baseline gap-2">
                    <span
                      data-testid={`pricing-price-${p.key}`}
                      className="text-[48px] leading-none font-extrabold tracking-tight text-[#0B1F33] tabular-nums"
                    >
                      {fmtEur(shownPrice)} €
                    </span>
                    <span className="text-[13px] text-[#5E6875]">HT / mois</span>
                  </div>
                  <div className="mt-2 text-[12.5px] text-[#5E6875] min-h-[18px]">
                    {annual ? (
                      <>Engagement annuel · 2 mois offerts · {fmtEur(p.yearly)} € HT/an</>
                    ) : (
                      <>Sans engagement</>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="mt-6 space-y-3 text-[14px] text-[#0B1F33] flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <span
                          className={`w-5 h-5 mt-0.5 rounded-full inline-flex items-center justify-center shrink-0 ${
                            p.highlight
                              ? "bg-[#FF9F1C] text-[#0B1F33]"
                              : "bg-[#0B1F33] text-white"
                          }`}
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                        <span className="leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA — pinned bottom, aligned across cards */}
                  <div className="mt-7">
                    <Link
                      to={`/inscription?plan=${planSlug(p)}`}
                      data-testid={`pricing-cta-${p.key}`}
                      className={`w-full inline-flex items-center justify-center gap-2 h-12 rounded-md font-extrabold text-[14px] transition-colors ${
                        p.highlight
                          ? "bg-[#FF9F1C] text-[#0B1F33] hover:bg-[#F58F06]"
                          : "bg-[#0B1F33] text-white hover:bg-[#0B1F33]/90"
                      }`}
                    >
                      {p.cta}
                    </Link>
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 text-center text-[12.5px] text-[#5E6875]">
          <p>14 jours pour tester, sans carte bancaire.</p>
        </Reveal>
      </div>
    </Section>
  );
}

/* ============ FINAL CTA ============ */
function FooterCta() {
  return (
    <Section className="relative bg-[#0B1F33] text-white py-24 lg:py-32 overflow-hidden">
      <img src={PHOTOS.chefKitchen} alt="Chef dans sa cuisine" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B1F33] via-[#0B1F33]/80 to-[#0B1F33]/70" />
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <Reveal as="h2" className="display-2 text-white">Essayez gratuitement, <MarkerLine>votre marge vous dira merci</MarkerLine>.</Reveal>
          <Reveal className="mt-6 text-[17px] text-white/80 max-w-[520px]"><p>14 jours pour tester, aucune carte bancaire demandée.</p></Reveal>
          <Reveal className="mt-8 flex flex-wrap gap-3">
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link to="/inscription?plan=monthly" className="btn-orange" data-testid="footer-cta">Essayez gratuitement <ArrowRight className="w-5 h-5" /></Link>
            </motion.div>
            <Link to="/connexion" className="btn-outline-white">Se connecter</Link>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-[#0B1F33] text-white/60 text-[13px] border-t border-white/8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-2.5"><Wordmark tone="dark" /></div>
        <div className="flex gap-6">
          <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
          <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
          <Link to="/cgu" className="hover:text-white">CGU</Link>
        </div>
        <div>© {new Date().getFullYear()} RentaScan</div>
      </div>
    </footer>
  );
}
